/**
 * ChatPanel · P2.6.b
 *
 * Renders the live chat timeline for one session. Subscribes to audit-store
 * via `selectSessionEvents` selector and filters to `chat.send` events.
 *
 * v0.1 scope:
 *   - Read-side only · agents drive the conversation
 *   - Auto-scroll to bottom on new events (unless user scrolled up)
 *   - Display: avatar (agent_id 首字母 / human icon) · name · body · ts
 *   - Connection state badge (open / reconnecting / etc) at top
 *
 * Out of scope (deferred to P3.x · §10 human-in-loop):
 *   - SPA-originated chat.send (human reply input)
 *   - @-mention agent handles
 *   - Threading by session_id parent
 *
 * codex-review 2026-04-29 P2.6.b R1:
 *   - P2 #1 fix · use design-system tokens · no hardcoded fallbacks per CLAUDE.md rule 2
 *   - P2 #2 fix · stricter audit_id / ts validation parallel to ws-client
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { translateError } from '@aiw/util';
import { useAuditStore, selectSessionEvents } from '../stores/audit-store';
import type { WsEvent } from '../lib/ws-client';
import { avatarGlyph, type AgentRow } from '../lib/fetch-agents';
import { eventsToNodes, type CanvasNode } from './CanvasView';
import { eventsToKnowledgeConflicts, eventsToKnowledgeItems, type CanvasKnowledgeItem } from './KnowledgeContext';
import { HumanActionPanel } from './HumanActionPanel';

export interface ChatMsg {
  audit_id: number;
  ts: number;
  agent_id: string | null;     // null = human · maybe future workspaces
  display_name: string;
  body: string;
  message_kind?: 'chat' | 'review_finding';
  finding_id?: string;
  severity?: 'structure_error' | 'semantic_concern' | 'info';
  finding_status?: 'open' | 'applied' | 'dismissed';
  has_auto_fix?: boolean;
}

/**
 * Filter + map raw audit events into chat messages. Pure function · exported
 * for unit testing. Drops events that aren't chat.send · have no body · or
 * fail strict validation (matches ws-client message-handler defense in depth ·
 * REST gap-fill could feed events that bypass the WS-side check).
 */
export function eventsToMessages(events: WsEvent[]): ChatMsg[] {
  const out: ChatMsg[] = [];
  const findingStatus = new Map<string, { status: 'applied' | 'dismissed'; audit_id: number }>();
  for (const evt of events) {
    if (evt.kind !== 'review.finding_applied' && evt.kind !== 'review.finding_dismissed') continue;
    if (typeof evt.audit_id !== 'number' || !Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) continue;
    const findingId = typeof evt.payload?.finding_id === 'string' ? evt.payload.finding_id : null;
    if (!findingId) continue;
    const status = evt.kind === 'review.finding_applied' ? 'applied' : 'dismissed';
    const prior = findingStatus.get(findingId);
    if (!prior || prior.audit_id < evt.audit_id) {
      findingStatus.set(findingId, { status, audit_id: evt.audit_id });
    }
  }
  for (const evt of events) {
    const m = eventToMsg(evt);
    if (m?.message_kind === 'review_finding' && m.finding_id) {
      const status = findingStatus.get(m.finding_id);
      if (status && status.audit_id >= m.audit_id) {
        m.finding_status = status.status;
      }
    }
    if (m) out.push(m);
  }
  out.sort((a, b) => a.audit_id - b.audit_id);
  return out;
}

function eventToMsg(evt: WsEvent): ChatMsg | null {
  if (evt.kind !== 'chat.send') return null;
  // Strict audit_id validation (codex R1 P2 fix · matches ws-client)
  if (typeof evt.audit_id !== 'number') return null;
  if (!Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) return null;
  if (typeof evt.ts !== 'number' || !Number.isFinite(evt.ts)) return null;

  const payload = evt.payload ?? {};
  // P2.7 e2e fix · backend audit_log payload uses `content_preview` (200-char
  // truncation per agent-tools.ts:411). Prior version read `body` and would
  // silently render empty for every real chat event. We accept both for
  // forward-compat (custom emitters or v0.2 may use `body` directly).
  const body = typeof payload.content_preview === 'string'
    ? payload.content_preview
    : (typeof payload.body === 'string' ? payload.body : '');
  if (!body) return null;

  // codex P2.8.C1 R1 P2 fix · backend now emits `sender_kind: 'user'` for
  // human chat (with agent_id=null + user_id). Render those as "You" so the
  // human-origin message in ChatPanel doesn't show as "unknown".
  const agent_id = typeof payload.agent_id === 'string' ? payload.agent_id : null;
  const sender_kind = typeof payload.sender_kind === 'string' ? payload.sender_kind : null;
  const user_id = typeof payload.user_id === 'string' ? payload.user_id : null;
  let display_name: string;
  if (typeof payload.display_name === 'string') {
    display_name = payload.display_name;
  } else if (agent_id) {
    display_name = agent_id;
  } else if (sender_kind === 'user') {
    // Show first 8 chars of user_id (UUID short prefix) · cleaner than full id
    display_name = user_id ? `You (${user_id.slice(0, 8)})` : 'You';
  } else {
    display_name = 'unknown';
  }
  const messageKind = payload.message_kind === 'review_finding' ? 'review_finding' : 'chat';
  const findingId = typeof payload.finding_id === 'string' ? payload.finding_id : undefined;
  const severity = payload.severity === 'structure_error' || payload.severity === 'semantic_concern' || payload.severity === 'info'
    ? payload.severity
    : undefined;
  const findingStatus = payload.status === 'open' || payload.status === 'applied' || payload.status === 'dismissed'
    ? payload.status
    : undefined;
  return {
    audit_id: evt.audit_id,
    ts: evt.ts,
    agent_id,
    display_name,
    body,
    message_kind: messageKind,
    finding_id: findingId,
    severity,
    finding_status: findingStatus,
    has_auto_fix: payload.auto_fix != null,
  };
}

function avatarInitials(name: string): string {
  return name.slice(0, 1).toUpperCase();
}

/**
 * 2026-05-13 dogfood fix · agent activity indicator
 *
 * Folds the task_run.* event stream into per-agent state · so ChatPanel
 * can show "α 正在思考 / 使用工具" pill above the input. Previously a
 * user sent a message and saw NOTHING until 30-60s later when Claude's
 * chat.send arrived · they'd assume the system was dead.
 *
 * Pure function · exported for unit testing.
 */
export type AgentActivityState = 'idle' | 'queued' | 'thinking' | 'tool-using';

export interface AgentActivity {
  agent_id: string;
  state: AgentActivityState;
  /** Active task_run id when state !== 'idle' · else null. */
  run_id: string | null;
  /** ts of the latest activity-altering event · for elapsed display. */
  started_ts: number;
  /** count of tool.called audits seen in this run · grows live. */
  tool_count: number;
}

export function deriveAgentActivity(events: WsEvent[]): Map<string, AgentActivity> {
  const byAgent = new Map<string, AgentActivity>();
  // Sort by audit_id to fold deterministically
  const sorted = [...events]
    .filter((e) => typeof e.audit_id === 'number' && Number.isSafeInteger(e.audit_id))
    .sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));

  for (const e of sorted) {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const agentId = typeof p.agent_id === 'string' ? p.agent_id : null;
    if (!agentId) continue;
    const runId = typeof p.run_id === 'string' ? p.run_id : null;
    const ts = typeof e.ts === 'number' && Number.isFinite(e.ts) ? e.ts : 0;
    const cur = byAgent.get(agentId) ?? {
      agent_id: agentId,
      state: 'idle' as AgentActivityState,
      run_id: null,
      started_ts: 0,
      tool_count: 0,
    };

    if (e.kind === 'task_run.created' || e.kind === 'task_run.queued_busy' || e.kind === 'task_run.waiting_worker' || e.kind === 'task_run.waiting_tree') {
      // New / queued / waiting · harness might be offline · NOT actively
      // running yet · use 'queued' so user sees 等待 harness 上线...
      if (cur.state === 'idle' || cur.run_id !== runId) {
        cur.state = 'queued';
        cur.run_id = runId;
        cur.started_ts = ts;
        cur.tool_count = 0;
      } else {
        if (cur.state !== 'tool-using' && cur.state !== 'thinking') cur.state = 'queued';
      }
    } else if (e.kind === 'task_run.dispatched') {
      // Harness picked up · Claude warming / inferring · 'thinking'
      if (cur.run_id !== runId) {
        cur.run_id = runId;
        cur.started_ts = ts;
        cur.tool_count = 0;
      }
      cur.state = 'thinking';
    } else if (e.kind === 'tool.called' && cur.run_id !== null) {
      // Tool dispatch (any run · doesn't carry run_id on payload usually · but
      // we want any tool.called from THIS agent while busy to bump the count)
      if (cur.state !== 'idle') {
        cur.state = 'tool-using';
        cur.tool_count += 1;
      }
    } else if (
      (e.kind === 'task_run.finished' ||
        e.kind === 'task_run.error' ||
        e.kind === 'task_run.canceled' ||
        e.kind === 'task_run.skipped' ||
        ((e.kind === 'task_run.cancel_requested' || e.kind === 'task_run.interrupt_requested') &&
          p.dispatched === false))
      && cur.run_id === runId
    ) {
      cur.state = 'idle';
      cur.run_id = null;
    }
    byAgent.set(agentId, cur);
  }
  return byAgent;
}

/**
 * Pull task_run.error events with non-empty error_detail · group by run_id ·
 * latest one wins · returns array sorted by audit_id DESC.
 *
 * Used for the inline error banner in ChatPanel · shows the most recent
 * failed run so user sees WHY their message produced no agent reply.
 * Only keeps errors from the last 5 minutes (newer ones supersede).
 */
export interface DerivedError {
  run_id: string;
  agent_id: string | null;
  audit_id: number;
  ts: number;
  error_detail: string;
}

export function deriveLatestErrors(events: WsEvent[]): DerivedError[] {
  const byRun = new Map<string, DerivedError>();
  for (const e of events) {
    if (e.kind !== 'task_run.error') continue;
    if (typeof e.audit_id !== 'number') continue;
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const runId = typeof p.run_id === 'string' ? p.run_id : null;
    if (!runId) continue;
    const detail = typeof p.error_detail === 'string' ? p.error_detail : '';
    if (!detail) continue;
    const ts = typeof e.ts === 'number' && Number.isFinite(e.ts) ? e.ts : 0;
    const prior = byRun.get(runId);
    if (!prior || prior.audit_id < e.audit_id) {
      byRun.set(runId, {
        run_id: runId,
        agent_id: typeof p.agent_id === 'string' ? p.agent_id : null,
        audit_id: e.audit_id,
        ts,
        error_detail: detail,
      });
    }
  }
  return [...byRun.values()].sort((a, b) => b.audit_id - a.audit_id);
}

export interface ChatAgentEntry {
  id: string;
  display: string;
  kind: AgentRow['agent_kind'];
  hint: string;
  harness_id: string | null;
}

export type ChatContextKind = 'node' | 'node_output' | 'artifact' | 'knowledge' | 'chat_summary';

export interface ChatContextChip {
  id: string;
  kind: ChatContextKind;
  label: string;
  detail: string;
  required: boolean;
  ref: Record<string, unknown>;
}

/** Fallback used by isolated tests / static render before CanvasPage injects `/api/agents` rows. */
const FALLBACK_AGENTS: ChatAgentEntry[] = [
  {
    id: 'alpha',
    display: 'α · Alpha',
    kind: 'claude_code',
    hint: 'Claude Code · 主写手',
    harness_id: null,
  },
];

const CONTEXT_KIND_LABEL: Record<ChatContextKind, string> = {
  node: '节点',
  node_output: '产出',
  artifact: '文件',
  knowledge: '知识',
  chat_summary: '摘要',
};

function formatTs(ts: number): string {
  // UTC server · UI shows Asia/Shanghai per A26
  const d = new Date(ts);
  return d.toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export interface ChatPanelProps {
  sessionId: string;
  workspaceId?: string;
  selectedNodeId?: string | null;
  /**
   * P2.8.C1 · backend base URL for the send POST. Pass `''` (or omit) to
   * use same-origin (Vite proxy in dev · same domain in prod). The
   * canvas page passes the resolved backendUrl already.
   */
  backendUrl?: string;
  /** SPA JWT for the send POST. Read from sessionStorage in production. */
  authToken?: string | null;
  /** Test-only · allow injecting a custom store hook for happy-dom-less test. */
  storeForTest?: typeof useAuditStore;
  /** Test-only · override fetch (mock backend in tests). */
  fetchImpl?: typeof fetch;
  /**
   * Registered ordinary agents that can be manually included in this session
   * chat. Canvas page fetches this from `/api/agents`; tests can inject it.
   */
  agents?: ChatAgentEntry[];
  agentsLoading?: boolean;
  agentsError?: string | null;
  joinedAgentIds?: string[];
  participantsLoading?: boolean;
  participantsError?: string | null;
  participantUpdatingIds?: string[];
  onJoinAgent?: (agentId: string) => void;
  onLeaveAgent?: (agentId: string) => void;
}

/** Parse @-mentions from message text · matches `@` + word chars (no spaces). */
export function extractMentions(text: string): string[] {
  const out: string[] = [];
  const re = /(?:^|\s)@([a-zA-Z0-9_-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[1]);
  return Array.from(new Set(out)); // dedupe
}

export function uniqueMentions(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const id = raw.startsWith('@') ? raw.slice(1) : raw;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function buildBodyWithParticipantMentions(body: string, participants: string[]): string {
  const trimmed = body.trim();
  const existing = new Set(extractMentions(trimmed));
  const missing = participants
    .map((id) => (id.startsWith('@') ? id.slice(1) : id))
    .filter((id) => id && !existing.has(id));
  if (missing.length === 0) return trimmed;
  return `${missing.map((id) => `@${id}`).join(' ')} ${trimmed}`.trim();
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function textValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function contextKind(value: unknown): ChatContextKind | null {
  if (value === 'node' || value === 'node_output' || value === 'artifact' || value === 'knowledge' || value === 'chat_summary') return value;
  return null;
}

function chipId(kind: ChatContextKind, key: string): string {
  return `${kind}:${key}`;
}

function chipFromNode(node: CanvasNode): ChatContextChip {
  const title = node.title ?? `${node.node_kind} ${node.node_id.slice(0, 8)}`;
  return {
    id: chipId('node', node.node_id),
    kind: 'node',
    label: title,
    detail: `${node.status}${node.assigned_agent_id ? ` · @${node.assigned_agent_id}` : ''}`,
    required: true,
    ref: {
      kind: 'node',
      ref_id: `node:${node.node_id}`,
      node_id: node.node_id,
      title,
      reason: '当前选中节点',
      required: true,
    },
  };
}

function contextRefChip(ref: unknown, idx: number, fallbackNode?: CanvasNode): ChatContextChip | null {
  const r = objectValue(ref);
  const kind = contextKind(r.kind) ?? contextKind(r.type);
  if (!kind) return null;
  const key =
    textValue(r.ref_id) ??
    textValue(r.node_id) ??
    textValue(r.output_id) ??
    textValue(r.artifact_id) ??
    textValue(r.knowledge_id) ??
    textValue(r.chat_summary_id) ??
    textValue(r.path) ??
    textValue(r.uri) ??
    `${fallbackNode?.node_id ?? 'ref'}:${idx}`;
  const title =
    textValue(r.title) ??
    textValue(r.path)?.split('/').filter(Boolean).pop() ??
    textValue(r.reason) ??
    key;
  return {
    id: chipId(kind, key),
    kind,
    label: title,
    detail: textValue(r.reason) ?? CONTEXT_KIND_LABEL[kind],
    required: r.required === true,
    ref: {
      ...r,
      kind,
      ref_id: textValue(r.ref_id) ?? `${kind}:${key}`,
      title,
      required: r.required === true,
    },
  };
}

function outputRowsFromEvents(events: WsEvent[], workspaceId?: string): Array<Record<string, unknown> & { audit_id: number }> {
  const rows: Array<Record<string, unknown> & { audit_id: number }> = [];
  for (const evt of events) {
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    if (evt.kind === 'workflow.snapshot') {
      const payload = objectValue(evt.payload);
      const outputs = Array.isArray(payload.node_outputs) ? payload.node_outputs : [];
      for (const output of outputs) {
        const record = objectValue(output);
        rows.push({ ...record, audit_id: typeof evt.audit_id === 'number' ? evt.audit_id : 0 });
      }
    } else if (evt.kind === 'node_output.recorded' || evt.kind === 'node.output_recorded') {
      const payload = objectValue(evt.payload);
      const output = objectValue(payload.output);
      rows.push({ ...(Object.keys(output).length > 0 ? output : payload), audit_id: evt.audit_id ?? 0 });
    }
  }
  return rows;
}

function chipFromNodeOutput(output: Record<string, unknown>): ChatContextChip | null {
  const outputId = textValue(output.output_id);
  const nodeId = textValue(output.node_id);
  const runId = textValue(output.run_id);
  const key = outputId ?? runId ?? nodeId;
  if (!key) return null;
  const summary = textValue(output.handoff_summary);
  return {
    id: chipId('node_output', key),
    kind: 'node_output',
    label: summary ? `总结 · ${summary.slice(0, 28)}` : `node_output ${key.slice(0, 8)}`,
    detail: nodeId ? `node ${nodeId.slice(0, 8)}` : '节点产出',
    required: true,
    ref: {
      kind: 'node_output',
      ref_id: `node_output:${key}`,
      output_id: outputId ?? undefined,
      run_id: runId ?? undefined,
      node_id: nodeId ?? undefined,
      title: summary ? summary.slice(0, 80) : undefined,
      reason: '上游/当前节点结构化产出',
      required: true,
    },
  };
}

function chipFromKnowledge(item: CanvasKnowledgeItem, required = false): ChatContextChip {
  const title = item.content.slice(0, 80);
  const source = item.source_node_id
    ? `node ${item.source_node_id.slice(0, 8)}`
    : item.source_kind;
  return {
    id: chipId('knowledge', item.item_id),
    kind: 'knowledge',
    label: title,
    detail: `${item.kind} · ${source}`,
    required,
    ref: {
      kind: 'knowledge',
      ref_id: item.item_id,
      knowledge_id: item.item_id,
      title,
      reason: item.scope_node_id ? '节点范围内已确认知识' : '会话/工作区已确认知识',
      required,
    },
  };
}

function activeKnowledgeForContext(events: WsEvent[], workspaceId?: string): CanvasKnowledgeItem[] {
  const openConflictItems = new Set<string>();
  for (const conflict of eventsToKnowledgeConflicts(events, workspaceId)) {
    if (conflict.status !== 'open') continue;
    openConflictItems.add(conflict.candidate_item_id);
    openConflictItems.add(conflict.existing_item_id);
  }
  return eventsToKnowledgeItems(events, workspaceId)
    .filter((item) => item.status === 'active' && !openConflictItems.has(item.item_id));
}

function artifactChipsFromOutput(output: Record<string, unknown>): ChatContextChip[] {
  const rawRefs = Array.isArray(output.artifact_refs) ? output.artifact_refs : [];
  const rawArtifacts = Array.isArray(output.artifacts) ? output.artifacts : [];
  const refs = rawRefs.length > 0
    ? rawRefs
    : rawArtifacts.map((path) => ({ kind: 'artifact', path }));
  return refs.flatMap((ref, idx): ChatContextChip[] => {
    const value = typeof ref === 'string'
      ? { kind: 'artifact', path: ref, required: false }
      : { ...objectValue(ref), kind: 'artifact', required: false };
    const chip = contextRefChip(value, idx);
    return chip ? [chip] : [];
  });
}

function dedupeChips(chips: ChatContextChip[]): ChatContextChip[] {
  const out: ChatContextChip[] = [];
  const seen = new Set<string>();
  for (const chip of chips) {
    if (seen.has(chip.id)) continue;
    seen.add(chip.id);
    out.push(chip);
  }
  return out;
}

export function buildChatContextChips(events: WsEvent[], workspaceId?: string, selectedNodeId?: string | null): ChatContextChip[] {
  if (!selectedNodeId) return [];
  const nodes = eventsToNodes(events, workspaceId);
  const node = nodes.find((n) => n.node_id === selectedNodeId);
  if (!node) return [];
  const chips: ChatContextChip[] = [chipFromNode(node)];
  node.context_refs.forEach((ref, idx) => {
    const chip = contextRefChip(ref, idx, node);
    if (chip) chips.push(chip);
  });
  const outputs = outputRowsFromEvents(events, workspaceId)
    .filter((row) => textValue(row.node_id) === selectedNodeId)
    .sort((a, b) => b.audit_id - a.audit_id);
  const latestOutput = outputs[0];
  if (latestOutput) {
    const outputChip = chipFromNodeOutput(latestOutput);
    if (outputChip) chips.push(outputChip);
    chips.push(...artifactChipsFromOutput(latestOutput));
  }
  const scopedKnowledge = activeKnowledgeForContext(events, workspaceId)
    .filter((item) =>
      item.scope_node_id === selectedNodeId ||
      item.source_node_id === selectedNodeId,
    );
  chips.push(...scopedKnowledge.map((item) => chipFromKnowledge(item, false)));
  return dedupeChips(chips);
}

export function buildContextPickerChips(events: WsEvent[], workspaceId?: string): ChatContextChip[] {
  const nodes = eventsToNodes(events, workspaceId).map(chipFromNode);
  const outputs = outputRowsFromEvents(events, workspaceId).flatMap((row): ChatContextChip[] => {
    const outputChip = chipFromNodeOutput(row);
    return outputChip ? [outputChip, ...artifactChipsFromOutput(row)] : artifactChipsFromOutput(row);
  });
  const knowledge = activeKnowledgeForContext(events, workspaceId).map((item) => chipFromKnowledge(item, false));
  return dedupeChips([...nodes, ...outputs, ...knowledge]);
}

/**
 * Detect whether the cursor is inside an `@xxx` mention token. If yes,
 * return the partial query (the part after `@` · "" if just `@`); else
 * null. Pure · exported for unit testing the popover trigger logic.
 *
 * Mention boundaries match extractMentions: `@` must be at string start
 * or right after whitespace · token chars are [a-zA-Z0-9_-]. Cursor must
 * be exactly at the end of the @-token (not past trailing whitespace).
 */
export function detectMentionAt(value: string, cursor: number): string | null {
  const before = value.slice(0, cursor);
  const m = before.match(/(?:^|\s)@([a-zA-Z0-9_-]*)$/);
  return m ? m[1] : null;
}

export function detectContextAt(value: string, cursor: number): string | null {
  const before = value.slice(0, cursor);
  const m = before.match(/(?:^|\s)#([a-zA-Z0-9_.:/-]*)$/);
  return m ? m[1] : null;
}

/**
 * Pure send helper · POST /api/sessions/:id/chat with parsed mentions ·
 * returns { ok, message_id?, error? }. Exported for unit testing the
 * URL/header/body shape (codex P2.8.C1 R1 P2 fix).
 */
export interface SendChatResult {
  ok: boolean;
  message_id?: string;
  audit_id?: number;
  error?: string;
}
export interface SendChatOpts {
  backendUrl: string;
  authToken: string;
  sessionId: string;
  body: string;
  mentions?: string[];
  context_refs?: Array<Record<string, unknown>>;
  scope?: 'session' | 'workspace';
  fetchImpl?: typeof fetch;
}
export async function sendChatMessage(opts: SendChatOpts): Promise<SendChatResult> {
  const fi = opts.fetchImpl ?? globalThis.fetch;
  const url = `${opts.backendUrl.replace(/\/$/, '')}/api/sessions/${encodeURIComponent(opts.sessionId)}/chat`;
  const trimmed = opts.body.trim();
  if (!trimmed) return { ok: false, error: 'empty body' };
  let resp: Response;
  try {
    const mentions = uniqueMentions([
      ...extractMentions(trimmed),
      ...(opts.mentions ?? []),
    ]);
    resp = await fi(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        body: trimmed,
        mentions: mentions.length > 0 ? mentions : undefined,
        context_refs: opts.context_refs && opts.context_refs.length > 0 ? opts.context_refs : undefined,
        scope: opts.scope ?? 'session',
      }),
    });
  } catch (e: any) {
    return { ok: false, error: String(e?.message ?? e) };
  }
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const err = await resp.json() as { error?: string };
      detail = err.error ?? detail;
    } catch { /* ignore */ }
    return { ok: false, error: detail };
  }
  try {
    const body = await resp.json() as { ok: boolean; message_id: string; audit_id: number };
    return { ok: true, message_id: body.message_id, audit_id: body.audit_id };
  } catch (e: any) {
    return { ok: false, error: `non-JSON response: ${String(e?.message ?? e)}` };
  }
}

export interface ReviewFindingActionOpts {
  backendUrl: string;
  authToken: string;
  sessionId: string;
  findingId: string;
  fetchImpl?: typeof fetch;
}

async function postReviewFindingAction(
  opts: ReviewFindingActionOpts,
  action: 'apply' | 'dismiss',
): Promise<{ ok: boolean; error?: string }> {
  const fi = opts.fetchImpl ?? globalThis.fetch;
  const url = `${opts.backendUrl.replace(/\/$/, '')}/api/sessions/${encodeURIComponent(opts.sessionId)}/review-findings/${encodeURIComponent(opts.findingId)}/${action}`;
  try {
    const resp = await fi(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const data = await resp.json() as { error?: string; error_code?: string };
        detail = data.error ?? data.error_code ?? detail;
      } catch { /* ignore */ }
      return { ok: false, error: detail };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

export function applyReviewFinding(opts: ReviewFindingActionOpts): Promise<{ ok: boolean; error?: string }> {
  return postReviewFindingAction(opts, 'apply');
}

export function dismissReviewFinding(opts: ReviewFindingActionOpts): Promise<{ ok: boolean; error?: string }> {
  return postReviewFindingAction(opts, 'dismiss');
}

export function ChatPanel({
  sessionId,
  workspaceId,
  selectedNodeId = null,
  backendUrl,
  authToken,
  storeForTest,
  fetchImpl,
  agents,
  agentsLoading = false,
  agentsError = null,
  joinedAgentIds,
  participantsLoading = false,
  participantsError = null,
  participantUpdatingIds = [],
  onJoinAgent,
  onLeaveAgent,
}: ChatPanelProps): JSX.Element {
  const useStore = storeForTest ?? useAuditStore;
  // useShallow · keeps the events array reference stable across renders
  // when the underlying filtered events haven't changed. Without it the
  // .filter() inside selectSessionEvents returns a new array every render,
  // zustand's strict-equality check fires, and we end up in an infinite
  // re-render loop (especially under React.StrictMode double-render in dev).
  const sessionEvents = useStore(useShallow(selectSessionEvents(sessionId)));
  const connectionState = useStore((s) => s.connectionState);

  const messages = useMemo(() => eventsToMessages(sessionEvents), [sessionEvents]);
  // 2026-05-13 dogfood · derive per-agent activity + latest errors so user
  // sees "α 正在思考 / 使用工具 (N)" + 错误翻译 instead of silent black box.
  const agentActivity = useMemo(() => deriveAgentActivity(sessionEvents), [sessionEvents]);
  const latestErrors = useMemo(() => deriveLatestErrors(sessionEvents), [sessionEvents]);
  // Tick every second so elapsed time on the activity pill updates live.
  const [now, setNow] = useState(() => Date.now());
  const anyBusy = useMemo(() => [...agentActivity.values()].some((a) => a.state !== 'idle'), [agentActivity]);
  useEffect(() => {
    if (!anyBusy) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyBusy]);

  // Auto-scroll to bottom when new messages arrive · unless user scrolled up
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const lastCount = useRef(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [reviewActionState, setReviewActionState] = useState<Record<string, 'applying' | 'dismissing' | 'applied' | 'dismissed' | `error:${string}`>>({});
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const suggestedContextChips = useMemo(
    () => buildChatContextChips(sessionEvents, workspaceId, selectedNodeId),
    [sessionEvents, workspaceId, selectedNodeId],
  );
  const pickerContextChips = useMemo(
    () => buildContextPickerChips(sessionEvents, workspaceId),
    [sessionEvents, workspaceId],
  );
  const allContextChips = useMemo(
    () => dedupeChips([...suggestedContextChips, ...pickerContextChips]),
    [suggestedContextChips, pickerContextChips],
  );
  const [selectedContextChipIds, setSelectedContextChipIds] = useState<string[]>([]);
  const visibleContextChips = useMemo(
    () => {
      const active = new Set(selectedContextChipIds);
      return dedupeChips([
        ...suggestedContextChips,
        ...allContextChips.filter((chip) => active.has(chip.id)),
      ]);
    },
    [suggestedContextChips, allContextChips, selectedContextChipIds],
  );
  const contextScope = `${workspaceId ?? ''}:${sessionId}:${selectedNodeId ?? ''}`;
  const lastContextScopeRef = useRef<string | null>(null);

  // @-mention popover + durable group-chat roster share the same agent list.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [contextQuery, setContextQuery] = useState<string | null>(null);
  const [contextIdx, setContextIdx] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mentionAgents = agents ?? FALLBACK_AGENTS;
  const joinedSet = useMemo(() => new Set(joinedAgentIds ?? selectedAgentIds), [joinedAgentIds, selectedAgentIds]);
  const filteredAgents = useMemo(
    () =>
      mentionQuery === null
        ? []
        : mentionAgents.filter((a) =>
            mentionQuery === ''
              ? true
              : a.id.toLowerCase().startsWith(mentionQuery.toLowerCase()),
          ),
    [mentionAgents, mentionQuery],
  );
  const filteredContextChips = useMemo(
    () => {
      if (contextQuery === null) return [];
      const q = contextQuery.toLowerCase();
      return allContextChips.filter((chip) =>
        q === ''
          ? true
          : `${chip.label} ${chip.detail} ${chip.id}`.toLowerCase().includes(q),
      ).slice(0, 8);
    },
    [allContextChips, contextQuery],
  );

  useEffect(() => {
    if (lastContextScopeRef.current !== contextScope) {
      setSelectedContextChipIds(suggestedContextChips.map((chip) => chip.id));
      lastContextScopeRef.current = contextScope;
      return;
    }
    const valid = new Set(allContextChips.map((chip) => chip.id));
    setSelectedContextChipIds((cur) => cur.filter((id) => valid.has(id)));
  }, [contextScope, suggestedContextChips, allContextChips]);

  useEffect(() => {
    if (!agents) return;
    const valid = new Set(agents.map((a) => a.id));
    setSelectedAgentIds((cur) => cur.filter((id) => valid.has(id)));
  }, [agents]);

  function toggleAgent(agentId: string): void {
    if (joinedAgentIds) {
      if (joinedSet.has(agentId)) {
        onLeaveAgent?.(agentId);
        setSelectedAgentIds((cur) => cur.filter((id) => id !== agentId));
      } else {
        onJoinAgent?.(agentId);
        setSelectedAgentIds((cur) => cur.includes(agentId) ? cur : [...cur, agentId]);
      }
      return;
    }
    setSelectedAgentIds((cur) =>
      cur.includes(agentId) ? cur.filter((id) => id !== agentId) : [...cur, agentId],
    );
  }

  function toggleAgentMention(agentId: string): void {
    setSelectedAgentIds((cur) =>
      cur.includes(agentId) ? cur.filter((id) => id !== agentId) : [...cur, agentId],
    );
  }

  function toggleContextChip(chipIdValue: string): void {
    setSelectedContextChipIds((cur) =>
      cur.includes(chipIdValue) ? cur.filter((id) => id !== chipIdValue) : [...cur, chipIdValue],
    );
  }

  function handleDraftChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    const v = e.target.value;
    setDraft(v);
    const q = detectMentionAt(v, e.target.selectionStart ?? v.length);
    const cq = q === null ? detectContextAt(v, e.target.selectionStart ?? v.length) : null;
    setMentionQuery(q);
    setContextQuery(cq);
    setMentionIdx(0);
    setContextIdx(0);
  }

  /** Insert agent at the @-token position · close popover. */
  function insertMention(agentId: string): void {
    const ta = inputRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? draft.length;
    const before = draft.slice(0, cursor);
    const after = draft.slice(cursor);
    // Replace the trailing @xxx with @<agentId> + trailing space
    const replaced = before.replace(/@([a-zA-Z0-9_-]*)$/, `@${agentId} `);
    const newCursor = replaced.length;
    const next = replaced + after;
    setDraft(next);
    setMentionQuery(null);
    // Restore focus + cursor after React flushes
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
    });
  }

  function insertContextChip(chip: ChatContextChip): void {
    const ta = inputRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? draft.length;
    const before = draft.slice(0, cursor);
    const after = draft.slice(cursor);
    const token = `#${chip.label.replace(/\s+/g, '-').slice(0, 28)} `;
    const replaced = before.replace(/#([a-zA-Z0-9_.:/-]*)$/, token);
    const newCursor = replaced.length;
    const next = replaced + after;
    setDraft(next);
    setSelectedContextChipIds((cur) => cur.includes(chip.id) ? cur : [...cur, chip.id]);
    setContextQuery(null);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
    });
  }

  useEffect(() => {
    if (autoScroll && messages.length > lastCount.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    lastCount.current = messages.length;
  }, [messages, autoScroll]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>): void {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
    setAutoScroll(atBottom);
  }

  async function handleSend(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (sending) return;
    if (!authToken) {
      setSendError('Not logged in');
      return;
    }
    setSending(true);
    setSendError(null);
    const activeRefs = allContextChips
      .filter((chip) => selectedContextChipIds.includes(chip.id))
      .map((chip) => chip.ref);
    const result = await sendChatMessage({
      backendUrl: backendUrl ?? '',
      authToken,
      sessionId,
      body: buildBodyWithParticipantMentions(draft, selectedAgentIds),
      mentions: selectedAgentIds,
      context_refs: activeRefs,
      fetchImpl,
    });
    setSending(false);
    if (!result.ok) {
      setSendError(result.error ?? 'send failed');
      return;
    }
    // Success · clear draft. The audit_log entry will arrive via WS push
    // and ChatPanel re-renders from the store.
    setDraft('');
    setSelectedContextChipIds(suggestedContextChips.map((chip) => chip.id));
  }

  async function handleReviewAction(msg: ChatMsg, action: 'apply' | 'dismiss'): Promise<void> {
    if (!authToken || !msg.finding_id) return;
    setReviewActionState((s) => ({
      ...s,
      [msg.finding_id!]: action === 'apply' ? 'applying' : 'dismissing',
    }));
    const fn = action === 'apply' ? applyReviewFinding : dismissReviewFinding;
    const result = await fn({
      backendUrl: backendUrl ?? '',
      authToken,
      sessionId,
      findingId: msg.finding_id,
      fetchImpl,
    });
    setReviewActionState((s) => ({
      ...s,
      [msg.finding_id!]: result.ok
        ? (action === 'apply' ? 'applied' : 'dismissed')
        : `error:${result.error ?? 'request failed'}`,
    }));
  }

  return (
    <section className="chat-panel panel">
      <div className="panel-head">
        <div className="panel-title-stack">
          <span className="panel-kicker">团队频道</span>
          <span className="title">群聊</span>
        </div>
        <span className="grow" />
        <ConnectionBadge state={connectionState} />
      </div>
      <AgentJoinBar
        agents={mentionAgents}
        joinedAgentIds={[...joinedSet]}
        selectedAgentIds={selectedAgentIds}
        updatingAgentIds={participantUpdatingIds}
        loading={agentsLoading || participantsLoading}
        error={agentsError ?? participantsError}
        onToggle={toggleAgent}
        onToggleMention={toggleAgentMention}
      />
      <HumanActionPanel
        sessionId={sessionId}
        backendUrl={backendUrl}
        authToken={authToken}
        events={sessionEvents}
        selectedNodeId={selectedNodeId}
        fetchImpl={fetchImpl}
      />
      <div className="panel-body" data-testid="chat-messages" ref={scrollRef} onScroll={handleScroll}>
        <div className="chat">
          {messages.length === 0 ? (
            <div className="chat-empty" data-testid="chat-empty-state">
              <strong>从一个目标开始</strong>
              <span>例如：@alpha 汇总五大商社并输出对比</span>
            </div>
          ) : (
            messages.map((m) => (
              <Message
                key={m.audit_id}
                msg={m}
                reviewActionState={m.finding_id ? reviewActionState[m.finding_id] : undefined}
                onReviewAction={handleReviewAction}
              />
            ))
          )}
        </div>
        <AgentActivityStrip activity={agentActivity} now={now} />
        <ErrorBanner errors={latestErrors} now={now} />
        <ContextChipBar
          chips={visibleContextChips}
          activeIds={selectedContextChipIds}
          onToggle={toggleContextChip}
        />
        <form data-testid="chat-input-form" onSubmit={handleSend} className="chat-compose">
          {sendError && (
            <div
              data-testid="chat-send-error"
              style={{
                position: 'absolute',
                top: -22,
                left: 8,
                right: 8,
                color: 'var(--accent-danger)',
                fontSize: 11,
              }}
            >
              Send failed · {sendError}
            </div>
          )}
          {mentionQuery !== null && filteredAgents.length > 0 && (
            <div className="mention-popover" role="listbox" data-testid="mention-popover">
              <div className="mp-head">@-mention agent</div>
              {filteredAgents.map((a, i) => (
                <button
                  key={a.id}
                  type="button"
                  role="option"
                  aria-selected={i === mentionIdx}
                  data-testid={`mention-item-${a.id}`}
                  className={`mp-item${i === mentionIdx ? ' active' : ''}`}
                  onMouseEnter={() => setMentionIdx(i)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep textarea focus
                    insertMention(a.id);
                  }}
                >
                  <span className="mp-id">@{a.id}</span>
                  <span className="mp-display">{a.display}</span>
                  <span className="mp-hint">{a.hint}</span>
                </button>
              ))}
            </div>
          )}
          {contextQuery !== null && filteredContextChips.length > 0 && (
            <div className="mention-popover context-popover" role="listbox" data-testid="context-popover">
              <div className="mp-head">上下文</div>
              {filteredContextChips.map((chip, i) => (
                <button
                  key={chip.id}
                  type="button"
                  role="option"
                  aria-selected={i === contextIdx}
                  data-testid={`context-item-${chip.id}`}
                  className={`mp-item${i === contextIdx ? ' active' : ''}`}
                  onMouseEnter={() => setContextIdx(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertContextChip(chip);
                  }}
                >
                  <span className="mp-id">#{CONTEXT_KIND_LABEL[chip.kind]}</span>
                  <span className="mp-display">{chip.label}</span>
                  <span className="mp-hint">{chip.detail}</span>
                </button>
              ))}
            </div>
          )}
          <textarea
            className="input"
            data-testid="chat-input"
            ref={inputRef}
            value={draft}
            onChange={handleDraftChange}
            onSelect={(e) => {
              const ta = e.currentTarget;
              const q = detectMentionAt(ta.value, ta.selectionStart ?? ta.value.length);
              const cq = q === null ? detectContextAt(ta.value, ta.selectionStart ?? ta.value.length) : null;
              setMentionQuery(q);
              setContextQuery(cq);
            }}
            onBlur={() => {
              setMentionQuery(null);
              setContextQuery(null);
            }}
            onKeyDown={(e) => {
              // Mention popover keyboard nav · only when popover open
              if (mentionQuery !== null && filteredAgents.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setMentionIdx((i) => (i + 1) % filteredAgents.length);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setMentionIdx((i) => (i - 1 + filteredAgents.length) % filteredAgents.length);
                  return;
                }
                if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  insertMention(filteredAgents[mentionIdx].id);
                  return;
                }
                if (e.key === 'Tab') {
                  e.preventDefault();
                  insertMention(filteredAgents[mentionIdx].id);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setMentionQuery(null);
                  return;
                }
              }
              if (contextQuery !== null && filteredContextChips.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setContextIdx((i) => (i + 1) % filteredContextChips.length);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setContextIdx((i) => (i - 1 + filteredContextChips.length) % filteredContextChips.length);
                  return;
                }
                if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  insertContextChip(filteredContextChips[contextIdx]);
                  return;
                }
                if (e.key === 'Tab') {
                  e.preventDefault();
                  insertContextChip(filteredContextChips[contextIdx]);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setContextQuery(null);
                  return;
                }
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void handleSend(e as unknown as React.FormEvent);
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend(e as unknown as React.FormEvent);
              }
            }}
            placeholder={
              authToken
                ? '输入消息，@agent 指派，#引用上下文'
                : '请先登录'
            }
            disabled={!authToken || sending}
            rows={2}
            aria-label="群聊消息"
          />
          <button
            type="submit"
            className="btn"
            data-testid="chat-send-button"
            disabled={!authToken || sending || !draft.trim()}
            title={authToken ? '发送' : '请先登录'}
          >
            {sending ? '发送中' : '发送'}
          </button>
        </form>
      </div>
    </section>
  );
}

function AgentJoinBar({
  agents,
  joinedAgentIds,
  selectedAgentIds,
  updatingAgentIds,
  loading,
  error,
  onToggle,
  onToggleMention,
}: {
  agents: ChatAgentEntry[];
  joinedAgentIds: string[];
  selectedAgentIds: string[];
  updatingAgentIds: string[];
  loading: boolean;
  error: string | null;
  onToggle: (agentId: string) => void;
  onToggleMention: (agentId: string) => void;
}): JSX.Element {
  const joined = new Set(joinedAgentIds);
  const mentioned = new Set(selectedAgentIds);
  const updating = new Set(updatingAgentIds);
  return (
    <div className="chat-agent-bar" data-testid="chat-agent-bar">
      <span className="chat-agent-bar-label">成员</span>
      <div className="chat-agent-list">
        {loading ? (
          <span className="chat-agent-state" data-testid="chat-agents-loading">加载中</span>
        ) : error ? (
          <span className="chat-agent-state danger" data-testid="chat-agents-error">{error}</span>
        ) : agents.length === 0 ? (
          <span className="chat-agent-state" data-testid="chat-agents-empty">无可用 agent</span>
        ) : (
          agents.map((agent) => {
            const active = joined.has(agent.id);
            const remind = mentioned.has(agent.id);
            const busy = updating.has(agent.id);
            return (
              <span key={agent.id} className="chat-agent-chip-wrap">
                <button
                  type="button"
                  className={`chat-agent-chip${active ? ' active' : ''}`}
                  data-testid={`chat-agent-chip-${agent.id}`}
                  aria-pressed={active}
                  disabled={busy}
                  onClick={() => onToggle(agent.id)}
                  title={`${agent.display} · ${agent.kind}${agent.harness_id ? ` · ${agent.harness_id}` : ''}`}
                >
                  <span className="chat-agent-avatar" aria-hidden>{avatarGlyph(agent.id)}</span>
                  <span className="chat-agent-id">@{agent.id}</span>
                  <span className="chat-agent-presence">{busy ? '…' : active ? '在场' : '加入'}</span>
                </button>
                {active && (
                  <button
                    type="button"
                    className={`chat-agent-mention${remind ? ' active' : ''}`}
                    data-testid={`chat-agent-mention-${agent.id}`}
                    aria-pressed={remind}
                    disabled={busy}
                    onClick={() => onToggleMention(agent.id)}
                    title={`本条消息 @${agent.id}`}
                  >
                    @
                  </button>
                )}
              </span>
            );
          })
        )}
      </div>
      {joinedAgentIds.length > 0 && (
        <span className="chat-agent-count" data-testid="chat-agent-selected-count">
          {joinedAgentIds.length}
        </span>
      )}
    </div>
  );
}

function ContextChipBar({
  chips,
  activeIds,
  onToggle,
}: {
  chips: ChatContextChip[];
  activeIds: string[];
  onToggle: (chipId: string) => void;
}): JSX.Element | null {
  if (chips.length === 0) return null;
  const active = new Set(activeIds);
  return (
    <div className="chat-context-bar" data-testid="chat-context-bar">
      <span className="chat-context-label">本轮上下文</span>
      <div className="chat-context-list">
        {chips.map((chip) => {
          const enabled = active.has(chip.id);
          return (
            <button
              key={chip.id}
              type="button"
              className={`chat-context-chip${enabled ? ' active' : ''}`}
              data-testid={`chat-context-chip-${chip.id}`}
              aria-pressed={enabled}
              onClick={() => onToggle(chip.id)}
              title={`${CONTEXT_KIND_LABEL[chip.kind]} · ${chip.detail}`}
            >
              <span className="chat-context-kind">{CONTEXT_KIND_LABEL[chip.kind]}</span>
              <span className="chat-context-text">{chip.label}</span>
              {chip.required ? <span className="chat-context-required">必</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Message({
  msg,
  reviewActionState,
  onReviewAction,
}: {
  msg: ChatMsg;
  reviewActionState?: string;
  onReviewAction?: (msg: ChatMsg, action: 'apply' | 'dismiss') => void;
}): JSX.Element {
  if (msg.message_kind === 'review_finding') {
    return (
      <ReviewFindingMessage
        msg={msg}
        actionState={reviewActionState}
        onAction={onReviewAction}
      />
    );
  }
  // Bubble color · agent vs agent-2 vs system per design
  // (alpha = blue agent · others = purple/green for visual variety)
  const isHuman = msg.agent_id === null;
  const bubbleClass = isHuman
    ? 'bubble system'
    : msg.agent_id === 'alpha' || msg.agent_id === 'α'
    ? 'bubble agent'
    : 'bubble agent-2';
  const tagClass = isHuman ? 'tag tag-human' : 'tag tag-bot';
  const tagLabel = isHuman ? '人' : 'bot';
  return (
    <div className="chat-msg" data-testid={`chat-msg-${msg.audit_id}`}>
      <Avatar name={msg.display_name} isHuman={isHuman} agentId={msg.agent_id} />
      <div className={bubbleClass} style={{ flex: 1, minWidth: 0 }}>
        <span className="who">
          {msg.display_name}
          <span className={tagClass}>{tagLabel}</span>
        </span>
        <span style={{ color: 'var(--fg-faint)', fontSize: 10, marginRight: 6 }}>
          {formatTs(msg.ts)}
        </span>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 2 }}>
          {renderBodyWithMentions(msg.body)}
        </div>
      </div>
    </div>
  );
}

function ReviewFindingMessage({
  msg,
  actionState,
  onAction,
}: {
  msg: ChatMsg;
  actionState?: string;
  onAction?: (msg: ChatMsg, action: 'apply' | 'dismiss') => void;
}): JSX.Element {
  const severityLabel =
    msg.severity === 'structure_error' ? '结构问题' :
    msg.severity === 'semantic_concern' ? '语义提醒' :
    '信息';
  const isBusy = actionState === 'applying' || actionState === 'dismissing';
  const resolved = actionState === 'applied' || actionState === 'dismissed' || msg.finding_status === 'applied' || msg.finding_status === 'dismissed';
  const error = actionState?.startsWith('error:') ? actionState.slice('error:'.length) : null;
  return (
    <div className="chat-msg" data-testid={`review-finding-${msg.finding_id ?? msg.audit_id}`}>
      <Avatar name={msg.display_name} isHuman={false} agentId={msg.agent_id} />
      <div
        className="bubble agent-2"
        style={{
          flex: 1,
          minWidth: 0,
          borderStyle: 'solid',
          borderColor: msg.severity === 'structure_error' ? 'var(--accent-danger)' : 'var(--accent-warning)',
          background: 'var(--bg-surface)',
        }}
      >
        <span className="who">
          {msg.display_name}
          <span className="tag tag-bot">{severityLabel}</span>
        </span>
        <span style={{ color: 'var(--fg-faint)', fontSize: 10, marginRight: 6 }}>
          {formatTs(msg.ts)}
        </span>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
          {msg.body}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn"
            data-testid={`review-apply-${msg.finding_id ?? msg.audit_id}`}
            disabled={isBusy || resolved || !msg.has_auto_fix || !onAction}
            onClick={() => onAction?.(msg, 'apply')}
            title={msg.has_auto_fix ? '接受修复' : '没有可自动应用的修复'}
          >
            {actionState === 'applying' ? '应用中…' : actionState === 'applied' || msg.finding_status === 'applied' ? '已接受' : '接受'}
          </button>
          <button
            type="button"
            className="btn ghost"
            data-testid={`review-dismiss-${msg.finding_id ?? msg.audit_id}`}
            disabled={isBusy || resolved || !onAction}
            onClick={() => onAction?.(msg, 'dismiss')}
            title="忽略"
          >
            {actionState === 'dismissing' ? '忽略中…' : actionState === 'dismissed' || msg.finding_status === 'dismissed' ? '已忽略' : '忽略'}
          </button>
          {error && (
            <span data-testid={`review-action-error-${msg.finding_id ?? msg.audit_id}`} style={{ color: 'var(--accent-danger)', fontSize: 11 }}>
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function renderBodyWithMentions(text: string): React.ReactNode {
  // Highlight @-mentions inline · same regex as extractMentions
  const parts: React.ReactNode[] = [];
  const re = /(@[a-zA-Z0-9_-]+)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <span key={`m-${key++}`} className="mention">
        {match[1]}
      </span>,
    );
    last = match.index + match[1].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : text;
}

function Avatar({
  name,
  isHuman,
  agentId,
}: {
  name: string;
  isHuman: boolean;
  agentId: string | null;
}): JSX.Element {
  if (!isHuman) {
    return (
      <span className="avatar bot" aria-hidden>
        {agentId === 'alpha' || agentId === 'α' ? 'α' : (agentId ?? '?').slice(0, 1)}
      </span>
    );
  }
  // Human · use one of a1/a2/a3 colors · seeded by name first char
  const variants = ['a1', 'a2', 'a3'] as const;
  const idx = name.charCodeAt(0) % variants.length;
  return (
    <span className={`avatar ${variants[idx]}`} aria-hidden>
      {avatarInitials(name)}
    </span>
  );
}

function ConnectionBadge({ state }: { state: string }): JSX.Element {
  // All colors via design-system tokens · no hex fallbacks (CLAUDE.md rule 2).
  const palette: Record<string, { color: string; label: string }> = {
    idle: { color: 'var(--fg-soft)', label: '待机' },
    connecting: { color: 'var(--accent-primary)', label: '连接中' },
    open: { color: 'var(--accent-success)', label: '已连接' },
    reconnecting: { color: 'var(--accent-warning)', label: '重连中' },
    closed: { color: 'var(--fg-soft)', label: '已关闭' },
  };
  const s = palette[state] ?? palette.idle;
  return (
    <span
      data-testid="connection-badge"
      data-state={state}
      style={{
        color: s.color,
        fontSize: 11,
        fontFamily: 'var(--font-body)',
        padding: '1px 6px',
        border: '1px solid currentColor',
        borderRadius: 4,
        opacity: 0.85,
      }}
    >
      ● {s.label}
    </span>
  );
}

/**
 * Inline strip showing per-agent live activity · one pill per busy agent.
 * Empty when all agents idle. Updates elapsed seconds via `now` prop tick.
 * 2026-05-13 dogfood fix · users couldn't tell if alpha was alive or stuck.
 */
function AgentActivityStrip({
  activity,
  now,
}: {
  activity: Map<string, AgentActivity>;
  now: number;
}): JSX.Element | null {
  const busy = [...activity.values()].filter((a) => a.state !== 'idle');
  if (busy.length === 0) return null;
  return (
    <div
      data-testid="agent-activity-strip"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        padding: '6px 12px',
        borderTop: '1px dashed var(--border-soft)',
        background: 'var(--bg-subtle)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
      }}
    >
      {busy.map((a) => {
        const elapsed = a.started_ts > 0 ? Math.max(0, Math.floor((now - a.started_ts) / 1000)) : 0;
        // 30s+ queued = harness likely offline · escalate to red w/ hint
        const isStuckQueued = a.state === 'queued' && elapsed > 30;
        const label =
          isStuckQueued ? '排队中 · harness 可能离线 · 检查进程' :
          a.state === 'queued' ? '排队中 · 等待 harness' :
          a.state === 'thinking' ? '正在思考' :
          a.state === 'tool-using' ? `调用工具 (${a.tool_count})` :
          '工作中';
        // 红 (stuck > 30s) / 黄 (queued) / 紫 (active)
        const color = isStuckQueued
          ? 'var(--accent-danger)'
          : a.state === 'queued'
            ? 'var(--accent-warning)'
            : 'var(--accent-primary)';
        return (
          <span
            key={a.agent_id}
            data-testid={`activity-${a.agent_id}`}
            data-state={a.state}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '2px 8px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--bg-surface)',
              border: `1px solid ${color}`,
              color,
              fontWeight: 600,
            }}
          >
            <span aria-hidden style={{ animation: 'pulse 1.4s ease-in-out infinite' }}>●</span>
            <span>α {a.agent_id} · {label}</span>
            <span style={{ color: 'var(--fg-soft)', fontWeight: 400 }}>{elapsed}s</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Inline error banner · shows the latest unresolved task_run.error per run.
 * Uses translateError from @aiw/util to render Chinese messageZh + suggested
 * action. Errors older than 5min are suppressed (likely resolved).
 *
 * 2026-05-13 dogfood fix · agents that failed silently confused users · this
 * surfaces "MCP 断开 / API 鉴权失败 / 配额超" etc. so the user knows what to do.
 */
function ErrorBanner({
  errors,
  now,
}: {
  errors: DerivedError[];
  now: number;
}): JSX.Element | null {
  const MAX_AGE_MS = 5 * 60 * 1000;
  const fresh = errors.filter((e) => now - e.ts < MAX_AGE_MS).slice(0, 1); // 仅显最新一条
  if (fresh.length === 0) return null;
  return (
    <div
      data-testid="chat-error-banner"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 12px',
        margin: '6px 8px 0',
        background: 'var(--bg-muted)',
        border: '1.5px solid var(--accent-danger)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
      }}
    >
      {fresh.map((e) => {
        const t = translateError(e.error_detail);
        return (
          <div key={e.run_id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--accent-danger)', fontWeight: 700 }}>
                ⚠ {e.agent_id ?? 'agent'} 失败
              </span>
              <span style={{ color: 'var(--fg-soft)', fontSize: 11 }}>
                · run {e.run_id.slice(0, 8)}…
              </span>
              <span style={{ flex: 1 }} />
              <span
                data-testid="chat-error-kind"
                style={{
                  padding: '0 6px',
                  borderRadius: 3,
                  fontSize: 10,
                  background: t.retryable ? 'var(--accent-warning)' : 'var(--bg-base)',
                  color: t.retryable ? '#fff' : 'var(--fg-soft)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {t.kind}
              </span>
            </div>
            <div style={{ color: 'var(--accent-danger)', fontWeight: 600 }}>{t.messageZh}</div>
            <div style={{ color: 'var(--fg-soft)' }}>建议：{t.suggestedAction}</div>
          </div>
        );
      })}
    </div>
  );
}
